import { Button, Classes, Dialog, FormGroup, Intent, Spinner } from '@blueprintjs/core'
import { FormFields, lang, toast } from 'botpress/shared'
import { UserProfile } from 'common/typings'
import React, { FC, useEffect, useState } from 'react'
import api from '~/app/api'
import packageJson from '../../../../package.json'
import { encryptPayload } from '../../aes-encryption'
import { jwtKeyStore, formDataStore } from '../../token-store'
import './modern-settings.css'

interface Props {
  isOpen: boolean
  profile: UserProfile
  toggle: () => void
  fetchProfile: () => void
}

const countryOptions = [
  { code: '+1', name: 'United States' },
  { code: '+91', name: 'India' },
  { code: '+44', name: 'United Kingdom' },
  { code: '+61', name: 'Australia' },
  { code: '+81', name: 'Japan' },
  { code: '+49', name: 'Germany' },
  { code: '+33', name: 'France' },
  { code: '+86', name: 'China' },
  { code: '+7', name: 'Russia' },
  { code: '+39', name: 'Italy' },
  { code: '+34', name: 'Spain' },
  { code: '+55', name: 'Brazil' },
  { code: '+27', name: 'South Africa' },
  { code: '+82', name: 'South Korea' },
  { code: '+62', name: 'Indonesia' },
  { code: '+234', name: 'Nigeria' },
  { code: '+20', name: 'Egypt' },
  { code: '+63', name: 'Philippines' },
  { code: '+90', name: 'Turkey' },
  { code: '+966', name: 'Saudi Arabia' }
]

const CURRENT_VERSION = packageJson.version
const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'

const UpdateUserProfile: FC<Props> = props => {
  const [fullName, setFullname] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [industryType, setIndustryType] = useState<string>('')
  const [organisationName, setOrganisationName] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [subIndustryType, setSubIndustryType] = useState<string>('')
  const [countryCode, setCountryCode] = useState<string>(countryOptions[0].code)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const token = jwtKeyStore.get() || ''
  const savedFormData = formDataStore.get() || {}

  useEffect(() => {
    setFullname(savedFormData.fullName || '')
    setEmail(savedFormData.email || '')
    setIndustryType(savedFormData.industryType || '')
    setOrganisationName(savedFormData.organisationName || '')
    setPhoneNumber(savedFormData.phoneNumber || '')
    setSubIndustryType(savedFormData.subIndustryType || '')
    setCountryCode(savedFormData.countryCode || countryOptions[0].code)
    setErrors({})
  }, [props.isOpen])

  const validate = (formData: any) => {
    const newErrors: { [key: string]: string } = {}

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required'
    }

    // Phone Number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone Number is required'
    } else {
      const phoneRegex = /^[0-9]{7,15}$/
      if (!phoneRegex.test(formData.phoneNumber.trim())) {
        newErrors.phoneNumber = 'Please enter valid phone number with no special characters or spaces'
      }
    }

    // Organisation Name validation
    if (!formData.organisationName.trim()) {
      newErrors.organisationName = 'Organisation Name is required'
    } else if (formData.organisationName.trim().length > 50) {
      newErrors.organisationName = 'Organization Name cannot exceed 50 characters'
    }

    return newErrors
  }

  const submit = async event => {
    event.preventDefault()

    // Remove these fields and add the rest
    const { numberOfBots, filteredBots, ...restFormData } = savedFormData
    const formData = {
      ...restFormData,
      fullName: fullName.trim(),
      organisationName: organisationName.trim(),
      phoneNumber: phoneNumber.trim(),
      countryCode: countryCode.trim()
    }

    const validationErrors = validate(formData)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      return
    }

    setIsLoading(true)
    try {
      const res = await dbCall(formData)
      if (!res.success) {
        toast.failure(res.msg)
        setIsLoading(false)
        return
      }

      props.toggle()
      toast.success(res.msg)
      formDataStore.set(formData)
      setTimeout(() => {
        window.location.reload()
      }, 200)
    } catch (err) {
      toast.failure(lang.tr('admin.errorUpdatingProfile', { msg: err.message }))
    } finally {
      setIsLoading(false)
    }
  }

  const dbCall = async (data) => {
    try {
      const result = await fetch(`${API_URL}/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({ payload: encryptPayload({ data }) }),
      })

      return result.json()
    } catch (error) {
      return { success: false, msg: error.message || 'Error saving data to db' }
    }
  }

  return (
    <>
      <Dialog
        title={lang.tr('admin.updateYourProfile')}
        icon="user"
        isOpen={props.isOpen}
        onClose={props.toggle}
        transitionDuration={0}
        canOutsideClickClose={false}
        className="modern-dialog"
        style={{ width: 550 }}
      >
        <form onSubmit={submit}>
          <div className={Classes.DIALOG_BODY}>
            <FormGroup
              label={'Full Name'}
              labelFor="input-fullname"
              className="modern-form-group"
              intent={errors.fullName ? Intent.DANGER : Intent.NONE}
              helperText={errors.fullName}
            >
              <input
                id="input-fullname"
                className={`modern-input ${errors.fullName ? 'bp3-intent-danger' : ''}`}
                value={fullName}
                onChange={e => setFullname(e.target.value)}
                tabIndex={1}
                autoFocus={true}
                disabled={isLoading}
              />
            </FormGroup>
            <FormGroup label={'Email'} labelFor="input-email" className="modern-form-group">
              <input
                id="input-email"
                className="modern-input modern-input-disabled"
                value={email}
                disabled
                tabIndex={2}
              />
            </FormGroup>

            <FormGroup
              label={'Organisation Name'}
              labelFor="input-organisation-name"
              className="modern-form-group"
              intent={errors.organisationName ? Intent.DANGER : Intent.NONE}
              helperText={errors.organisationName}
            >
              <input
                id="input-organisation-name"
                className={`modern-input ${errors.organisationName ? 'bp3-intent-danger' : ''}`}
                value={organisationName}
                onChange={e => setOrganisationName(e.target.value)}
                tabIndex={4}
                disabled={isLoading}
              />
            </FormGroup>

            <FormGroup
              label={'Phone Number'}
              labelFor="input-phone-number"
              className="modern-form-group"
              intent={errors.phoneNumber ? Intent.DANGER : Intent.NONE}
              helperText={errors.phoneNumber}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                <select
                  value={countryCode}
                  onChange={e => setCountryCode(e.target.value)}
                  className="modern-select"
                  style={{ width: 100 }}
                  tabIndex={5}
                  disabled={isLoading}
                >
                  {countryOptions.map(opt => (
                    <option key={opt.code} value={opt.code}>
                      {opt.code} ({opt.name})
                    </option>
                  ))}
                </select>
                <input
                  id="input-phone-number"
                  className={`modern-input ${errors.phoneNumber ? 'bp3-intent-danger' : ''}`}
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  tabIndex={6}
                  style={{ flex: 1 }}
                  disabled={isLoading}
                />
              </div>
            </FormGroup>

            <div style={{ display: 'flex', gap: 16 }}>
              <FormGroup label={'Industry Type'} labelFor="input-industry-type" className="modern-form-group" style={{ flex: 1 }}>
                <input
                  id="input-industry-type"
                  className="modern-input modern-input-disabled"
                  value={industryType}
                  disabled
                  tabIndex={7}
                />
              </FormGroup>

              <FormGroup label={'Sub-Industry Type'} labelFor="input-sub-industry-type" className="modern-form-group" style={{ flex: 1 }}>
                <input
                  id="input-sub-industry-type"
                  className="modern-input modern-input-disabled"
                  value={subIndustryType}
                  disabled
                  tabIndex={8}
                />
              </FormGroup>
            </div>
          </div>
          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button
                id="btn-submit-update-user"
                type="submit"
                text={lang.tr('save')}
                tabIndex={9}
                className="modern-btn-primary"
                disabled={isLoading}
              />
            </div>
          </div>
        </form>
        {isLoading && (
          <div className="modern-loader">
            <Spinner size={50} />
            <div className="modern-loader-text">Updating your profile...</div>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default UpdateUserProfile


